// @story HIR-HR2
// PipelineBuilder — Enhancement #1: Configurable Assessment Pipeline.
// Recruiters build a role-specific assessment funnel per JD: add stages from
// a catalog, reorder, enable/disable, set passing score + modality.
//
// Backend:
//   GET  /api/jobs/assessment-catalog
//   GET  /api/jobs/:jobId/pipeline
//   PUT  /api/jobs/:jobId/pipeline   { pipeline: [...] }
import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import AssessmentSetupDrawer from "./AssessmentSetupDrawer";
import StageReadiness from "./StageReadiness";
import api from "../../services/api";
import { resumeApi } from "../../services/resumeApi";
import config from "../../config/config";
import { toast } from "react-toastify";
import useMessagingHold from "../../hooks/useMessagingHold";
import MessagingHoldBanner from "../../components/common/messagingHold/MessagingHoldBanner";
import { Button, Card, CardHeader, Checkbox, EmptyState, Icon, Input, Modal, Select, StatusPill, Switch } from "../../ds";
import { Loading, Note } from "../../components/page";
// Each stage type's design-system icon.
const TYPE_ICON = {
  pre_screening: 'chat-circle-dots', final_interview: 'sparkle', psychoanalytical: 'brain',
  gen_ai_readiness: 'robot',
  aptitude: 'pulse', technical: 'list-checks', coding: 'desktop', communication: 'chat-circle-dots',
  ai_interview: 'sparkle', case_study: 'file-text', custom: 'wrench', situational_judgement: 'brain',
  questionnaire: 'clipboard-text', human_interview: 'users',
};
// A request for a new assessment type, as a pill.
const REQUEST_TONE = { pending: 'warning', approved: 'success', rejected: 'danger' };
const MODALITIES = ["inherit", "audio", "text"];
// Interview/agent-based types run on prompts — no material upload needed
// Only assessment types (aptitude, technical, case_study, custom) support material upload
const INTERVIEW_TYPES = new Set([
  'pre_screening', 'final_interview', 'ai_interview',
  'communication', 'psychoanalytical', 'coding',
  'gen_ai_readiness', 'situational_judgement', 'ai_audio_interview',
]);
// These types have their own dedicated builder UI — not material upload or interview brief
const CUSTOM_BUILDER_TYPES = new Set(['questionnaire', 'human_interview']);
// Mirrors server DRIVER_FOR_TYPE — which interview engine actually runs a stage.
const ENGINE_FOR_TYPE = {
  pre_screening:    "Pre-screening engine",
  final_interview:  "Final interview engine",
  psychoanalytical: "Psychometric agent",
  coding:           "Coding agent",
  gen_ai_readiness: "Gen AI readiness agent",
  communication:    "AI interviewer",
  ai_interview:     "AI interviewer",
  ai_audio_interview: "Final interview engine (spoken)",
  technical:        "AI interviewer",
  aptitude:         "AI interviewer",
  case_study:       "AI interviewer",
  custom:           "AI interviewer",
};
let _uid = 0;
const tempId = () => `new-${Date.now()}-${_uid++}`;
// @story HIR-HR2
// Ready-made scorecards a panel can load onto a human-interview stage, so the
// marking parameters and their weightages match the school's own sheets.
// @story HIR-TC1
const SCORECARD_PRESETS = {
  teaching_demo: [
    { label: 'Use of Technology', maxMarks: 5 },
    { label: 'Relevance of Lesson Established', maxMarks: 5 },
    { label: 'Appropriate use of Smart Board & Green Board', maxMarks: 5 },
    { label: 'Communication Skill', maxMarks: 5 },
    { label: 'Ownership of Learning with Students', maxMarks: 10 },
    { label: 'Classroom Management', maxMarks: 15 },
    { label: 'Formative Assessment', maxMarks: 5 },
    { label: 'Relationship with Class', maxMarks: 5 },
    { label: "Teacher's Reflection", maxMarks: 15 },
  ],
  interview_40: [
    { label: 'Personality / Attitude', maxMarks: 10 },
    { label: 'Language Proficiency', maxMarks: 10 },
    { label: 'Work Methodology / Pedagogy', maxMarks: 10 },
    { label: 'Subject Proficiency', maxMarks: 10 },
  ],
};
/* Which of the group's standard stages a stage is, for the track's weights
   in the recruitment process (HIR-GL1), and the group rubric a panel stage is
   marked on (HIR-TC3, HIR-TC4). Set by a person, never read from the name. */
const STANDARD_STAGES = [
  ["", "Not a standard stage"], ["written", "Written test"], ["demo", "Demo"], ["admin_round", "Admin round"],
  ["preliminary", "Preliminary interview"], ["interview2_p1", "Interview 2, phase 1"], ["interview2_p2", "Interview 2, phase 2"],
  ["day_at_dps", "A Day at DPS (no marks)"], ["society", "DPS Society round (no marks)"], ["exit_interview", "Exit interview (no marks)"],
];
const RUBRICS = [["", "The scorecard below"], ["demo", "The group's demo rubric (70 marks)"], ["interview", "The group's interview rubric for the post's track (1 to 4)"]];
const sumMarks = (criteria) => (criteria || []).reduce((a, c) => a + (Number(c.maxMarks) || 0), 0);
const PipelineBuilder = ({ jobId, onSaved }) => {
  // While the school's candidate messages are paused, stage links only open
  // for test candidates (the server refuses everyone else), so only they are
  // offered here.
  const hold = useMessagingHold();
  const [catalog, setCatalog] = useState([]);
  const [stages, setStages]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [dirty, setDirty]     = useState(false);
  // The pipeline as last loaded or saved, so "Discard changes" can put it back.
  const savedSnapshot = useRef([]);
  /* Removing a stage that is already saved needs a separate yes. The trash
     icon only edits this screen; on Save the server lists the saved stages
     that would go, with how many candidates are in each, and nothing is saved
     until "Yes, remove these stages" is ticked. { removed, message } | null */
  const [removal, setRemoval] = useState(null);
  const [removalTicked, setRemovalTicked] = useState(false);
  // Design brief per stage
  const [showBriefFor, setShowBriefFor]         = useState(null); // stageId showing brief form
  // Request new assessment type
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestForm, setRequestForm]           = useState({ name:'', description:'', purpose:'', evaluationMethod:'' });
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [myRequests, setMyRequests]             = useState([]);
  // Exam rules editor per stage
  const [showRulesFor, setShowRulesFor] = useState(null); // stageId
  // Assessment setup drawer
  const [setupStage, setSetupStage] = useState(null); // full stage object
  const [setupTab, setSetupTab] = useState("setup");
  // Bumped when a stage's panel closes or the pipeline is saved, so the
  // readiness grid reads again (@story HIR-HR21).
  const [readyKey, setReadyKey] = useState(0);
  /* A link can open one stage's panel (29 Oct 2026): ?stage=<stageId> — from
     the Assessment approvals list — and &write=1 opens its editor ("Write it
     myself"). The link's stage is opened once, then taken off the address. */
  const [searchParams, setSearchParams] = useSearchParams();
  const openStage = (s, tab = "setup") => { setSetupTab(tab); setSetupStage(s); };
  const closeStage = () => {
    setSetupStage(null);
    setReadyKey((k) => k + 1);
    if (searchParams.get("stage")) {
      const next = new URLSearchParams(searchParams);
      next.delete("stage"); next.delete("write");
      setSearchParams(next, { replace: true });
    }
  };
  // Candidate journey links
  const [candidates, setCandidates] = useState([]);
  const [selectedCand, setSelectedCand] = useState("");
  const [copiedKey, setCopiedKey] = useState("");
  // Candidate-messaging templates for this school (from the Messages module).
  // Every stage messages the candidate, so each needs an email + WhatsApp
  // template set — this drives the "template not set" alert below.
  const [templates, setTemplates] = useState({ email: [], whatsapp: [] });
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/jobs/${jobId}/pipeline`);
        const d = r.data?.data || {};
        setCatalog(d.catalog || []);
        const loaded = (d.pipeline || []).map((s) => ({ ...s, _key: s.stageId || tempId() }));
        savedSnapshot.current = loaded;
        setStages(loaded);
      } catch (e) {
        // Fall back to the standalone catalog if the job has no pipeline yet
        try {
          const c = await api.get(`/jobs/assessment-catalog`);
          setCatalog(c.data?.data || []);
        } catch (_) { toast.error("Couldn't load assessment catalog"); }
      } finally {
        setLoading(false);
      }
    })();
  }, [jobId]);
  useEffect(() => {
    const want = searchParams.get("stage");
    if (!want || loading || setupStage) return;
    const s = stages.find((x) => x.stageId === want);
    if (s) openStage(s, searchParams.get("write") ? "questions" : "setup");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, loading, stages]);
  // The school's message templates, so each stage can be given an email + a
  // WhatsApp template and we can flag any stage that has none set. Best-effort:
  // if it can't load, the selectors fall back to showing the saved key.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/school-settings/message-templates');
        const a = r.data?.data?.available || {};
        setTemplates({ email: a.email || [], whatsapp: a.whatsapp || [] });
        setTemplatesLoaded(true);
      } catch (_) { /* non-fatal — the selectors just show the saved key */ }
    })();
  }, []);
  // Pull a few shortlisted candidates so the recruiter can grab journey links.
  useEffect(() => {
    (async () => {
      try {
        const r = await resumeApi.getAllResumes(jobId, 1, 50, "shortlisted");
        setCandidates(r?.data?.resumes || []);
      } catch (_) { /* non-fatal — links section just hides */ }
    })();
  }, [jobId]);
  const linkCandidates = hold.paused ? candidates.filter((c) => c.isTestCandidate) : candidates;
  useEffect(() => {
    if (!linkCandidates.some((c) => c._id === selectedCand)) setSelectedCand(linkCandidates[0]?._id || "");
  }, [linkCandidates, selectedCand]);
  const savedStages = stages.filter((s) => s.stageId && !String(s.stageId).startsWith("new-"));
  const linkFor = (stageId) =>
    `${config.baseUrl}/stage-interview/${jobId}/${selectedCand}/${stageId}`;
  // The copied link is the candidate's own signed one, from the server.
  const copyLink = async (stageId) => {
    try {
      const r = await api.get(`/jobs/${jobId}/pipeline-stage/${stageId}/candidates/${selectedCand}/link`);
      await navigator.clipboard.writeText(r.data?.data?.link || linkFor(stageId));
      setCopiedKey(stageId);
      setTimeout(() => setCopiedKey(""), 1500);
    } catch (e) { toast.info(e.response?.data?.message || "Copy failed — select the link text manually."); }
  };
  const addStage = (type) => {
    const cat = catalog.find((c) => c.type === type);
    setStages((s) => [...s, {
      _key: tempId(), stageId: tempId(), type,
      label: cat?.label || type, enabled: true, order: s.length,
      passingScore: 60, modality: "inherit", brief: "", promptKind: "",
      stagePrompt: "",
    }]);
    setDirty(true);
  };
  const update = (key, patch) => {
    setStages((s) => s.map((st) => (st._key === key ? { ...st, ...patch } : st)));
    setDirty(true);
  };
  const remove = (key) => { setStages((s) => s.filter((st) => st._key !== key)); setDirty(true); };
  const setCriteria = (key, criteria) => update(key, { criteria });
  const move = (i, dir) => {
    setStages((s) => {
      const j = i + dir;
      if (j < 0 || j >= s.length) return s;
      const next = [...s];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
    setDirty(true);
  };
  /* The old in-row upload and question editor are gone (29 Oct 2026): they
     put questions straight onto a stage. A stage's paper is now uploaded,
     written or edited in its panel ("Configure assessment") and goes to the
     school's approver as a proposal first. */
  // ── Request new type handlers ────────────────────────────────────────────
  const fetchMyRequests = async () => {
    try {
      const r = await api.get('/jobs/assessment-requests');
      setMyRequests(r.data?.data || []);
    } catch (_) {}
  };
  const submitRequest = async () => {
    const { name, description, purpose, evaluationMethod } = requestForm;
    if (!name || !description || !purpose || !evaluationMethod) {
      toast.error('All fields are required'); return;
    }
    try {
      setSubmittingRequest(true);
      await api.post('/jobs/assessment-requests', requestForm);
      toast.success('Request submitted — admin will review shortly');
      setShowRequestModal(false);
      setRequestForm({ name:'', description:'', purpose:'', evaluationMethod:'' });
      fetchMyRequests();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Submission failed');
    } finally { setSubmittingRequest(false); }
  };
  const discard = () => {
    setStages(savedSnapshot.current);
    setDirty(false);
    setRemoval(null);
    setRemovalTicked(false);
  };
  // Any further edit makes the list on the panel out of date: close it, so
  // the next Save asks again about exactly what would then be removed.
  useEffect(() => { setRemoval(null); setRemovalTicked(false); }, [stages]);
  // Cancel on the removal panel: put back the stages that were going to be
  // removed, where they were, and keep every other change.
  const keepRemovedStages = () => {
    const goingIds = new Set((removal?.removed || []).map((r) => r.stageId));
    setStages((cur) => {
      const next = [...cur];
      savedSnapshot.current.forEach((st, i) => {
        if (goingIds.has(st.stageId) && !next.some((x) => x.stageId === st.stageId)) {
          next.splice(Math.min(i, next.length), 0, st);
        }
      });
      return next;
    });
    setRemoval(null);
    setRemovalTicked(false);
  };
  // Leaving the page with unsaved changes would silently lose them (a reorder
  // looks done on screen but is not saved until "Save pipeline").
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  const save = async (acknowledgeRemovals) => {
    setSaving(true);
    try {
      const payload = {
        pipeline: stages.map((s, i) => ({
          stageId: s.stageId?.startsWith("new-") ? undefined : s.stageId,
          type: s.type, label: s.label, enabled: s.enabled, order: i,
          passingScore: Number(s.passingScore) || 60, modality: s.modality,
          brief: s.brief || "", promptKind: s.promptKind || "",
          stagePrompt: s.stagePrompt || "",
          emailTemplateKey: s.emailTemplateKey || "", waTemplateKey: s.waTemplateKey || "",
          deadlineDays: Number(s.deadlineDays) || 3,
          criteria: (s.criteria || []).map((c) => ({ label: c.label, maxMarks: Number(c.maxMarks) || 0 })),
          weightKey: s.weightKey || "",
          ...(s.type === "human_interview" ? { rubricKey: s.rubricKey || "" } : {}),
        })),
        ...(Array.isArray(acknowledgeRemovals) ? { acknowledgeRemovals } : {}),
      };
      const r = await api.put(`/jobs/${jobId}/pipeline`, payload);
      const saved = r.data?.data?.pipeline || [];
      const savedStagesList = saved.map((s) => ({ ...s, _key: s.stageId }));
      savedSnapshot.current = savedStagesList;
      setStages(savedStagesList);
      setDirty(false);
      setRemoval(null);
      setRemovalTicked(false);
      toast.success("Pipeline saved");
      setReadyKey((k) => k + 1);
      if (onSaved) onSaved();
    } catch (e) {
      const body = e.response?.data;
      if (e.response?.status === 409 && body?.code === "removal_needs_acknowledgement") {
        // Nothing was saved. Show which stages would go and ask first.
        setRemoval({ removed: body.removed || [], message: body.message || "" });
        setRemovalTicked(false);
        return;
      }
      toast.error(body?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  // Per-stage messaging readiness from the loaded template lists. Every stage
  // messages the candidate, so a stage is only "ready" once it has an email
  // AND a WhatsApp template set (and present in Messages). If the lists can't
  // be read we fall back to "is a key set" so a gateway blip doesn't falsely
  // flag everything.
  const stageComms = (s) => {
    const has = (list, key) => Boolean(key) && (!templatesLoaded || list.some((t) => t.key === key));
    const emailOk = has(templates.email, s.emailTemplateKey);
    const waOk = has(templates.whatsapp, s.waTemplateKey);
    return { emailOk, waOk, complete: emailOk && waOk };
  };
  const commsIncomplete = stages.filter((s) => s.enabled !== false && !stageComms(s).complete);
  if (loading) {
    return <Card><Loading label="Loading pipeline" /></Card>;
  }
  // The small line under a stage's name: which kind of stage it is.
  const kindOf = (type) => (type === 'questionnaire'
    ? 'HR Questionnaire'
    : type === 'human_interview'
    ? 'Human Interview (Google Meet)'
    : INTERVIEW_TYPES.has(type)
    ? 'AI Interview'
    : 'Q&A Exam (upload material)');
  const caption = { fontSize: 'var(--fs-caption)', color: 'var(--text-muted)' };
  const tile = { borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--surface-2)', padding: '12px 14px' };
  const saveButton = (onClick, disabled) => (
    <Button onClick={onClick} disabled={disabled} icon={<Icon name={saving ? 'spinner' : 'check-circle'} spin={saving} />}>
      {saving ? "Saving…" : "Save pipeline"}
    </Button>
  );
  return (
    <>
    <div style={{ display: 'grid', gap: 24, minWidth: 0 }}>
      <MessagingHoldBanner paused={hold.paused} loading={hold.loading} />
      <Card>
        <CardHeader
          title="Assessment Pipeline"
          action={saveButton(() => save(), saving || !dirty)}
        />
        <p style={{ margin: 0, fontSize: 'var(--fs-body-sm)', color: 'var(--text-2)' }}>
          Build a role-specific funnel for this job. Use the arrows to change the order, switch stages on or off, and set a pass bar per stage. Changes are kept only when you press Save pipeline.
          {stages.length === 0 && " No stages added yet — add assessments from the catalog below."}
        </p>
      </Card>
      {/* Is every stage's test or interview brief ready? (@story HIR-HR21) */}
      {savedStages.length > 0 && (
        <StageReadiness jobId={jobId} refreshKey={readyKey}
          onOpen={(stageId) => { const s = stages.find((x) => x.stageId === stageId); if (s) openStage(s); }} />
      )}
      {/* Messaging-completeness alert: every stage messages the candidate, and
          those messages must go through the Messages module as templates. A
          stage with no email + WhatsApp template set is not complete. */}
      {stages.length > 0 && commsIncomplete.length > 0 && (
        <Note tone="warning">
          <p style={{ margin: 0, fontWeight: 600 }}>
            {commsIncomplete.length} stage{commsIncomplete.length > 1 ? "s" : ""} not ready to message candidates
          </p>
          <p style={{ margin: '4px 0 0' }}>
            Every stage emails and WhatsApps the candidate when they reach it — and those must go through
            the Messages module. Set an <strong>Email</strong> and a <strong>WhatsApp</strong> template for each
            stage below. Create templates in the <strong>Messages app</strong> first if the list is empty.
          </p>
          <p style={{ margin: '4px 0 0', fontSize: 'var(--fs-caption)' }}>
            Needs a template: {commsIncomplete.map((s) => s.label || s.type).join(", ")}
          </p>
        </Note>
      )}
      {/* Stage list */}
      <div style={{ display: 'grid', gap: 12 }}>
        {stages.length === 0 && (
          <Card>
            <EmptyState
              icon={<Icon name="list-checks" size={36} />}
              title="No stages yet"
              description="No stages yet. Add assessments from the catalog below."
            />
          </Card>
        )}
        {stages.map((s, i) => {
          const iconName = TYPE_ICON[s.type] || 'sparkle';
          const c = stageComms(s);
          const missingOpt = (list, key) =>
            key && !list.some((t) => t.key === key)
              ? [{ value: key, label: `${key} (not in Messages)` }] : [];
          const tplOptions = (list, key) => [
            { value: '', label: '— not set —' },
            ...list.map((t) => ({ value: t.key, label: t.key })),
            ...missingOpt(list, key),
          ];
          return (
            <Card key={s._key} padding="16px 20px" style={{ opacity: s.enabled ? 1 : 0.6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                {/* Order controls */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Button size="sm" variant="ghost" aria-label="Move up" onClick={() => move(i, -1)} disabled={i === 0}
                    icon={<Icon name="caret-down" style={{ transform: 'rotate(180deg)' }} />} />
                  <Button size="sm" variant="ghost" aria-label="Move down" onClick={() => move(i, +1)} disabled={i === stages.length - 1}
                    icon={<Icon name="caret-down" />} />
                </div>
                <div style={{ width: 40, height: 40, borderRadius: 'var(--radius-md)', background: 'var(--brand-primary-soft)', color: 'var(--brand-primary-ink)', display: 'grid', placeItems: 'center', flex: 'none' }}>
                  <Icon name={iconName} size={20} />
                </div>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="num" style={{ ...caption, fontWeight: 600, width: 18 }}>{i + 1}</span>
                    <Input
                      value={s.label}
                      onChange={(e) => update(s._key, { label: e.target.value })}
                    />
                  </div>
                  <div style={{ ...caption, marginLeft: 26, marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ textTransform: 'capitalize' }}>{s.type.replace(/_/g, " ")}</span>
                    <span aria-hidden="true">•</span>
                    <span style={{ color: 'var(--text-2)', fontWeight: 500 }}>{kindOf(s.type)}</span>
                  </div>
                </div>
                {/* Pass score */}
                <div style={{ width: 84 }}>
                  <Input type="number" value={s.passingScore} hint="pass %"
                    onChange={(e) => update(s._key, { passingScore: e.target.value })} />
                </div>
                {/* Modality */}
                <div style={{ width: 120 }}>
                  <Select value={s.modality} onChange={(v) => update(s._key, { modality: v })}
                    options={MODALITIES.map((m) => ({ value: m, label: m.charAt(0).toUpperCase() + m.slice(1) }))} />
                </div>
                {/* Enable toggle */}
                <Switch checked={!!s.enabled} onChange={() => update(s._key, { enabled: !s.enabled })} label={s.enabled ? "On" : "Off"} />
                <Button size="sm" variant="ghost" aria-label="Remove stage" onClick={() => remove(s._key)} icon={<Icon name="trash" />} />
              </div>
              <div style={{ display: 'grid', gap: 10, marginTop: 12, marginLeft: 52 }}>
                {/* @story HIR-GL1 */}
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 240 }}>
                    <Select label="Standard stage" value={s.weightKey || ""} onChange={(v) => update(s._key, { weightKey: v })}
                      options={STANDARD_STAGES.map(([value, label]) => ({ value, label }))}
                      hint="Its weight comes from the recruitment process for the post's track." />
                  </div>
                  {s.type === 'human_interview' && (
                    <div style={{ minWidth: 300 }}>
                      <Select label="Marked on" value={s.rubricKey || ""} onChange={(v) => update(s._key, { rubricKey: v })}
                        options={RUBRICS.map(([value, label]) => ({ value, label }))} />
                    </div>
                  )}
                </div>
                {/* ── Human interview: info + scorecard the panel marks ── */}
                {s.type === 'human_interview' && (
                  <>
                    <Note tone="info">
                      <strong>Human Interview stage.</strong> After saving, assign candidates to the AI or Human track from the candidate list. The panel fills in the scorecard below during the interview.
                    </Note>
                    {/* @story HIR-TC1 */}
                    {/* Scorecard editor */}
                    {(() => {
                      const crit = s.criteria || [];
                      const total = sumMarks(crit);
                      const setRow = (ci, patch) => setCriteria(s._key, crit.map((c, x) => (x === ci ? { ...c, ...patch } : c)));
                      return (
                        <div style={tile}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                            <span style={{ fontSize: 'var(--fs-body-sm)', fontWeight: 600, color: 'var(--text-1)' }}>Scorecard — parameters the panel marks</span>
                            <span className="num" style={caption}>Total {total} marks</span>
                          </div>
                          {crit.length === 0 && (
                            <p style={{ ...caption, margin: '0 0 8px' }}>No parameters yet — load a ready card or add your own.</p>
                          )}
                          <div style={{ display: 'grid', gap: 6 }}>
                            {crit.map((c, ci) => (
                              <div key={ci} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ flex: 1 }}>
                                  <Input value={c.label} placeholder="Parameter (e.g. Classroom Management)"
                                    onChange={(e) => setRow(ci, { label: e.target.value })} />
                                </div>
                                <div style={{ width: 80 }}>
                                  <Input type="number" value={c.maxMarks}
                                    onChange={(e) => setRow(ci, { maxMarks: e.target.value })} />
                                </div>
                                <span style={{ ...caption, width: 36 }}>marks</span>
                                <Button size="sm" variant="ghost" aria-label="Remove parameter" icon={<Icon name="trash" />}
                                  onClick={() => setCriteria(s._key, crit.filter((_, x) => x !== ci))} />
                              </div>
                            ))}
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 8 }}>
                            <Button size="sm" variant="ghost" icon={<Icon name="plus" />}
                              onClick={() => setCriteria(s._key, [...crit, { label: '', maxMarks: 0 }])}>Add parameter</Button>
                            <span style={caption}>Load ready card:</span>
                            <Button size="sm" variant="secondary"
                              onClick={() => setCriteria(s._key, SCORECARD_PRESETS.teaching_demo.map((c) => ({ ...c })))}>Teaching Demo /70</Button>
                            <Button size="sm" variant="secondary"
                              onClick={() => setCriteria(s._key, SCORECARD_PRESETS.interview_40.map((c) => ({ ...c })))}>Interview /40</Button>
                          </div>
                          {total > 0 && (
                            <p style={{ ...caption, margin: '8px 0 0' }}>
                              The panel's total is scored as a % of {total} and checked against the {s.passingScore}% pass bar.
                            </p>
                          )}
                        </div>
                      );
                    })()}
                  </>
                )}
                {/* ── Configure button for assessment + questionnaire stages ── */}
                {(s.type === 'questionnaire' || (!INTERVIEW_TYPES.has(s.type) && !CUSTOM_BUILDER_TYPES.has(s.type))) && (
                  <div>
                    {s.stageId?.startsWith('new-') ? (
                      <p style={{ margin: 0, fontSize: 'var(--fs-caption)', color: 'var(--status-warning-text)' }}>
                        ⚠ Save the pipeline first to configure this assessment
                      </p>
                    ) : (
                      <Button size="sm" variant="secondary" icon={<Icon name="gear" />} onClick={() => openStage(s)}>
                        Configure assessment
                      </Button>
                    )}
                  </div>
                )}
                {/* ── The AI interview's brief (goal, questions, rubric): written and approved in the same panel (30 Oct 2026) ── */}
                {s.type === 'ai_audio_interview' && !s.stageId?.startsWith('new-') && (
                  <div>
                    <Button size="sm" variant="secondary" icon={<Icon name="gear" />} onClick={() => openStage(s)}>
                      Interview brief
                    </Button>
                  </div>
                )}
                {/* ── Candidate messaging templates (required for every stage) ── */}
                <div style={{ ...tile, borderColor: c.complete ? 'var(--border)' : 'var(--status-warning-text)', background: c.complete ? 'var(--surface-2)' : 'var(--status-warning-bg)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 'var(--fs-body-sm)', fontWeight: 600, color: 'var(--text-1)' }}>
                    Candidate messaging
                    <StatusPill tone={c.complete ? 'success' : 'warning'}>
                      {c.complete ? "✓ ready" : "— Email & WhatsApp template needed"}
                    </StatusPill>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
                    <Select label="Email template" value={s.emailTemplateKey || ""}
                      onChange={(v) => update(s._key, { emailTemplateKey: v })}
                      options={tplOptions(templates.email, s.emailTemplateKey)} placeholder="— not set —" />
                    <Select label="WhatsApp template" value={s.waTemplateKey || ""}
                      onChange={(v) => update(s._key, { waTemplateKey: v })}
                      options={tplOptions(templates.whatsapp, s.waTemplateKey)} placeholder="— not set —" />
                  </div>
                  {/* @story HIR-CN2 — a stage the candidate completes arrives with a deadline */}
                  {s.type !== "human_interview" ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 10, fontSize: 'var(--fs-body-sm)', color: 'var(--text-2)' }}>
                      Candidate has
                      <div style={{ width: 80 }}>
                        <Input type="number" value={s.deadlineDays ?? 3}
                          onChange={(e) => update(s._key, { deadlineDays: e.target.value })} />
                      </div>
                      days to complete it — the date goes into the invitation.
                    </div>
                  ) : (
                    <p style={{ ...caption, margin: '10px 0 0' }}>
                      The invitation goes when a candidate is given an interview slot. The reminder and the
                      "interview moved" message are set once for the school in Messages → Communications.
                    </p>
                  )}
                  {templatesLoaded && templates.email.length === 0 && templates.whatsapp.length === 0 && (
                    <p style={{ margin: '6px 0 0', fontSize: 'var(--fs-caption)', color: 'var(--status-warning-text)' }}>No templates yet — create them in the Messages app.</p>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
      {/* Catalog */}
      <Card>
        <CardHeader
          title="Add assessment"
          action={
            <Button size="sm" variant="secondary" icon={<Icon name="plus" />}
              onClick={() => { setShowRequestModal(true); fetchMyRequests(); }}>
              Request New Type
            </Button>
          }
        />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
          {catalog.map((c) => (
            <button key={c.type} onClick={() => addStage(c.type)}
              className="eq-pipeline-add"
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, padding: '12px 14px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--surface-1)', cursor: 'pointer', textAlign: 'left', font: 'inherit' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', color: 'var(--brand-primary-ink)' }}>
                <Icon name={TYPE_ICON[c.type] || 'sparkle'} />
                {c.isCustom && <StatusPill tone="warning" dot={false}>CUSTOM</StatusPill>}
                <span style={{ marginLeft: 'auto' }}><Icon name="plus" /></span>
              </span>
              <span style={{ fontWeight: 600, fontSize: 'var(--fs-body-sm)', color: 'var(--text-1)' }}>{c.label}</span>
              <span style={{ ...caption, lineHeight: 1.3 }}>{c.desc}</span>
            </button>
          ))}
        </div>
        <style>{`.eq-pipeline-add:hover{border-color:var(--brand-primary);background:var(--surface-2)}`}</style>
      </Card>
      {/* Candidate journey links — share a stage directly with a candidate */}
      {savedStages.length > 0 && (
        <Card>
          <CardHeader title="Share with candidates" />
          <p style={{ margin: '0 0 16px', fontSize: 'var(--fs-body-sm)', color: 'var(--text-2)' }}>
            Each saved stage has a direct candidate link. Pick a candidate, then copy the link for the stage you want them to take.
            {dirty && " Save your changes first to get links for new stages."}
          </p>
          {hold.paused && (
            <div style={{ marginBottom: 12 }}>
              <Note tone="warning">
                Candidate messages are paused, so links are offered only for test candidates. Other candidates' links would not open.
              </Note>
            </div>
          )}
          {linkCandidates.length === 0 ? (
            <div style={{ fontSize: 'var(--fs-body-sm)', color: 'var(--text-muted)' }}>
              {hold.paused && candidates.length
                ? "No test candidates among the shortlisted. Mark one as a test candidate from their profile to try the stages."
                : "No shortlisted candidates yet for this role."}
            </div>
          ) : (
            <>
              <div style={{ maxWidth: 320, marginBottom: 16 }}>
                <Select
                  value={selectedCand}
                  onChange={setSelectedCand}
                  options={linkCandidates.map((c) => ({
                    value: c._id,
                    label: `${c.candidateName || c.email || c._id}${c.isTestCandidate ? " (test candidate)" : ""}`,
                  }))}
                />
              </div>
              <div style={{ display: 'grid', gap: 8 }}>
                {savedStages.map((s, i) => (
                  <div key={s.stageId} style={{ ...tile, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '8px 12px' }}>
                    <span className="num" style={{ ...caption, fontWeight: 600, width: 18 }}>{i + 1}</span>
                    <span style={{ fontWeight: 600, fontSize: 'var(--fs-body-sm)', color: 'var(--text-1)', width: 176, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.label}</span>
                    <input
                      readOnly
                      value={selectedCand ? linkFor(s.stageId) : "Select a candidate…"}
                      onFocus={(e) => e.target.select()}
                      style={{ flex: 1, minWidth: 160, fontSize: 'var(--fs-caption)', fontFamily: 'var(--font-mono, monospace)', color: 'var(--text-2)', background: 'var(--surface-1)', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', padding: '6px 8px', textOverflow: 'ellipsis' }}
                    />
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => copyLink(s.stageId)}
                      disabled={!selectedCand}
                      icon={<Icon name={copiedKey === s.stageId ? 'check-circle' : 'clipboard-text'} />}
                    >
                      {copiedKey === s.stageId ? "Copied" : "Copy"}
                    </Button>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>
      )}
      {/* ── Request New Assessment Type Modal ── */}
      <Modal
        open={showRequestModal}
        onClose={() => setShowRequestModal(false)}
        title="Request New Assessment Type"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowRequestModal(false)}>Cancel</Button>
            <Button onClick={submitRequest} disabled={submittingRequest}
              icon={<Icon name={submittingRequest ? 'spinner' : 'paper-plane-right'} spin={submittingRequest} />}>
              {submittingRequest ? 'Submitting…' : 'Submit Request'}
            </Button>
          </>
        }
      >
        <p style={{ margin: '0 0 16px', fontSize: 'var(--fs-body-sm)', color: 'var(--text-2)' }}>Submit a request to your admin. Approved types will appear in your catalog exclusively.</p>
        <div style={{ display: 'grid', gap: 12 }}>
          {[
            { field:'name', label:'Assessment Name', placeholder:'e.g. Domain Knowledge Test' },
            { field:'description', label:'Description', placeholder:'What does this assessment evaluate?' },
            { field:'purpose', label:'Purpose', placeholder:'Why is this needed for this role?' },
            { field:'evaluationMethod', label:'Evaluation Method', placeholder:'How will candidates be evaluated?' },
          ].map(({ field, label, placeholder }) => (
            <Input key={field} id={`request-${field}`} label={label} required multiline rows={field === 'name' ? 1 : 2}
              value={requestForm[field]}
              onChange={e => setRequestForm(f => ({ ...f, [field]: e.target.value }))}
              placeholder={placeholder} />
          ))}
        </div>
        {/* My past requests */}
        {myRequests.length > 0 && (
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
            <h4 style={{ margin: '0 0 8px', fontSize: 'var(--fs-body-sm)', fontWeight: 600, color: 'var(--text-muted)' }}>My Requests</h4>
            <div style={{ display: 'grid', gap: 6, maxHeight: 144, overflowY: 'auto' }}>
              {myRequests.map(r => (
                <div key={r._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 'var(--fs-body-sm)', ...tile, padding: '8px 12px' }}>
                  <span style={{ fontWeight: 500, color: 'var(--text-1)' }}>{r.name}</span>
                  <StatusPill tone={REQUEST_TONE[r.status] || 'neutral'}>{r.status}</StatusPill>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>
      {dirty && removal && (
        <div style={{ position: 'sticky', bottom: 0, zIndex: 20, background: 'var(--status-warning-bg)', color: 'var(--status-warning-text)', border: '1px solid var(--status-warning-text)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-lg)', padding: '12px 16px' }}>
          <p style={{ margin: 0, fontSize: 'var(--fs-body-sm)', fontWeight: 600 }}>
            Saving will remove {removal.removed.length === 1 ? "this stage" : "these stages"} from the pipeline:
          </p>
          <ul style={{ margin: '8px 0 0', paddingLeft: 18, display: 'grid', gap: 4 }}>
            {removal.removed.map((r) => (
              <li key={r.stageId} style={{ fontSize: 'var(--fs-body-sm)', color: 'var(--status-danger-text)' }}>
                <span style={{ textDecoration: 'line-through', fontWeight: 500 }}>{r.label}</span>
                <span style={{ marginLeft: 8 }}>
                  {r.candidatesInStage
                    ? `${r.candidatesInStage} candidate${r.candidatesInStage === 1 ? " is" : "s are"} in this stage`
                    : "No candidate is in this stage yet"}
                </span>
              </li>
            ))}
          </ul>
          {removal.message && <p style={{ margin: '8px 0 0', fontSize: 'var(--fs-caption)' }}>{removal.message.replace(/\s*Nothing was saved\..*$/, "")}</p>}
          <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <Checkbox
              checked={removalTicked}
              onChange={(v) => setRemovalTicked(v)}
              label="Yes, remove these stages"
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Button size="sm" variant="secondary" onClick={keepRemovedStages} disabled={saving}>
                Cancel
              </Button>
              <Button size="sm" variant="secondary" onClick={discard} disabled={saving}>
                Discard changes
              </Button>
              {saveButton(() => save(removal.removed.map((r) => r.stageId)), saving || !removalTicked)}
            </div>
          </div>
        </div>
      )}
      {dirty && !removal && (
        <div style={{ position: 'sticky', bottom: 0, zIndex: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', background: 'var(--surface-1)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-lg)', padding: '12px 16px' }}>
          <span style={{ fontSize: 'var(--fs-body-sm)', fontWeight: 500, color: 'var(--status-warning-text)' }}>You have unsaved changes to this pipeline.</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button size="sm" variant="secondary" onClick={discard} disabled={saving}>
              Discard changes
            </Button>
            {saveButton(() => save(), saving)}
          </div>
        </div>
      )}
    </div>
    {/* Assessment Setup Drawer */}
    {setupStage && (
      <AssessmentSetupDrawer
        jobId={jobId}
        stage={setupStage}
        initialTab={setupTab}
        onClose={closeStage}
        onSaved={closeStage}
      />
    )}
    </>
  );
};
export default PipelineBuilder;
